/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import { ApiError, GetNodeConfigHistoryResponse, NodeConfigHistoryFilters } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseNodeConfigHistoryQueryProps = {
  nodeId: string;
  filters?: NodeConfigHistoryFilters;
  pageSize?: number;
  staleTime?: number;
  enabled?: boolean;
};

/**
 * Custom hook that fetches node configuration history.
 *
 * @param props.nodeId - Unique identifier of the node
 * @param props.filters - Optional filters (status, operationalState, mode)
 * @param props.enabled - Whether to enable the query (default: true)
 * @returns React Query result containing config history data
 *
 * @example
 * ```tsx
 * // Fetch all config history
 * const { data } = useNodeConfigHistoryQuery({ nodeId: '123' });
 *
 * // Fetch only standard mode configs
 * const { data } = useNodeConfigHistoryQuery({
 *   nodeId: '123',
 *   filters: { mode: 'standard' }
 * });
 * ```
 */
export function useNodeConfigHistoryQuery({
  nodeId,
  filters,
  pageSize = 10,
  staleTime,
  enabled = true,
}: UseNodeConfigHistoryQueryProps) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return useQuery<GetNodeConfigHistoryResponse, ApiError>({
    queryKey: ['nodeConfigHistory', selectedTenantId, nodeId, filters, pageSize],
    queryFn: () => {
      return mgmtPlaneAPIClient.nodes.getNodeConfigHistory({
        tenantId: selectedTenantId!,
        nodeId,
        filters,
        pageSize,
        sort: ['-dateCreated'], // Most recent first
      });
    },
    staleTime,
    enabled: enabled && !!selectedTenantId && !!nodeId,
  });
}
